import {
  Alert,
  AlertColor,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import PageLoader from 'components/PageLoader';
import { MouseEvent, RefObject, useRef, useState } from 'react';
import axiosInstance from 'utils/axios';
import { removeErrorFieldsFromValues } from 'utils/validators';
import { Edit } from '@mui/icons-material';
import Notification from 'components/Notification';
import { CountryCode } from 'libphonenumber-js';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import { fetchFromStorage } from 'utils/storage';
import { CustomerDetailsEditForm } from './CustomerDetailsEditForm';
import { KycFormButton } from '../../Customer/AddEditCustomer/Kyc';

type CustomerDetailsEditProps = {
  bookingId: number;
  onSuccess: () => void;
  openDrawer: boolean;
  setOpenDrawer: (arg: boolean) => void;
  isCompany: boolean;
  customerName: string;
  isDeleted: boolean;
  actualizationFinalized?: boolean;
  /** When true, only render edit control (name shown elsewhere). */
  hideNameRow?: boolean;
};

export const CustomerDetailsEdit: React.FC<CustomerDetailsEditProps> = ({
  bookingId,
  onSuccess,
  openDrawer,
  setOpenDrawer,
  isCompany,
  customerName,
  isDeleted,
  actualizationFinalized = false,
  hideNameRow = false,
}) => {
  const IsPrivilege = useSelector(Privilege);
  const canEditCustomer =
    IsPrivilege.includes(privileges.editBooking) && !isDeleted && !actualizationFinalized;
  const { id } = useParams() as any;
  const defaultCountry = (fetchFromStorage('defaultCountryCode') || 'IN') as CountryCode;
  const theme = useTheme();
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [country, setCountry] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [isCaptureLoyaltyDetails, setIsCaptureLoyaltyDetails] = useState<boolean>(false);
  const [captureKyc, setCaptureKyc] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  let formRef: RefObject<Form | null | undefined> = useRef();

  const getInitialResponse = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.customerDetails(id));
      const transformedData = {
        ...data,
        city: data?.city?.value,
        state: data?.state?.value,
        country: data?.country?.value,
        loyaltyName: data?.loyaltyProgram?.name,
        loyaltyReferenceNo: data?.loyaltyProgram?.referenceNo,
        loyaltyRemark: data?.loyaltyProgram?.remark,
      };

      if (data?.country?.value) await getState(data?.country?.value);
      if (data?.state?.value) await getCities(data?.state?.value);

      setIsCaptureLoyaltyDetails(data.captureLoyaltyProgram);
      setCaptureKyc(data.captureKyc);
      setPageResponse(transformedData);
      return transformedData;
    } catch (error) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const getCountry = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.countries);
      setCountry(data.map((item: any) => ({ label: item.name, value: item.id })));
    } catch (error) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const getState = async (id: number) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.states(id));
      setStates(data.map((item: any) => ({ label: item.name, value: item.id })));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const getCities = async (id: number) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.cities(id));
      setCities(data.map((item: any) => ({ label: item.name, value: item.id })));
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCatch = (error: any) => {
    setLoading(false);
    if (error.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data?.error_description
          ? error.response.data.error_description
          : error.response.data.message,
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  };

  const handleOpen = async () => {
    if (id) {
      try {
        setLoading(true);
        const [details] = await Promise.all([
          getInitialResponse(),
          getCountry(),
        ]);
        setPageResponse({ ...details });
        setOpenDrawer(true);
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setHasError(false);
    formRef?.current?.resetForm();
  };

  const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const cleanedData = removeErrorFieldsFromValues(formData) as any;

    if (isFormValid) {
      const requestBody = {
        ...cleanedData,
        id: id,
        city: cleanedData.city || null,
        state: cleanedData.state || null,
        country: cleanedData.country || null,
        phone:
          cleanedData.phone.value === ''
            ? {
                countryCode: null,
                value: null,
              }
            : cleanedData.phone,
        pinCode: cleanedData.pinCode === '' ? null : cleanedData.pinCode,
        panNumber: cleanedData.panNumber === '' ? null : cleanedData.panNumber,
        loyaltyProgram:
          cleanedData.loyaltyReferenceNo || cleanedData.loyaltyName || cleanedData.loyaltyRemark
            ? {
                referenceNo: cleanedData.loyaltyReferenceNo === '' ? null : cleanedData.loyaltyReferenceNo,
                name: cleanedData.loyaltyName === '' ? null : cleanedData.loyaltyName,
                remark: cleanedData.loyaltyRemark === '' ? null : cleanedData.loyaltyRemark,
              }
            : null,
      };

      try {
        setLoading(true);
        await axiosInstance.put(URLS.customerDetails(id), requestBody);
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message,
        });
        onSuccess();
        handleClose();
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    } else {
      setHasError(true);
    }
  };

  const handleChange = (key: string, value: any, formData: any) => {
    if (key === 'country') {
      getState(value);
      setPageResponse({ ...formData, id: pageResponse.id, state: '', city: '', country: value });
    }
    if (key === 'state') {
      getCities(value);
      setPageResponse({ ...formData, id: pageResponse.id, city: '', state: value });
    }
  };

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {hideNameRow ? (
        canEditCustomer && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title={strings.updateCustomerDetails} enterDelay={400}>
              <IconButton
                size="small"
                onClick={handleOpen}
                aria-label={strings.updateCustomerDetails}
                color="primary">
                <Edit sx={{ fontSize: '20px' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      ) : screenSizeDownMd ? (
        <Box display="flex" alignItems={'center'}>
          <Typography>{customerName}</Typography>
          {canEditCustomer && (
            <Button onClick={handleOpen}>
              <Edit sx={{ fontSize: '17px' }} />
            </Button>
          )}
        </Box>
      ) : (
        <Box display="flex" alignItems={'center'} justifyContent={'space-between'}>
          <Typography>{customerName}</Typography>
          {canEditCustomer && (
            <Button variant="outlined" onClick={handleOpen} endIcon={<Edit sx={{ fontSize: '17px' }} />}>
              {strings.updateCustomerDetails}
            </Button>
          )}
        </Box>
      )}
      <DrawerMenu
        width="70%"
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={strings.updateCustomerDetails}>
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Form
            onChange={handleChange}
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={CustomerDetailsEditForm(
              strings,
              country,
              states,
              cities,
              true,
              defaultCountry,
              isCaptureLoyaltyDetails,
            )}
            values={pageResponse}
          />
        </Grid>
        <Grid item xs={12} sm={3} lg={2}>
          <Button fullWidth variant="contained" sx={{ color: '#ffffff' }} onClick={handleSave}>
            {strings.common_button_save}
          </Button>
        </Grid>

        {captureKyc && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <KycFormButton bookingId={bookingId} />
            </Grid>
          </Grid>
        )}
      </DrawerMenu>
    </>
  );
};
